/* eslint-disable new-cap */
import { BlueprintInstanceType } from '../shared.enums';
import { KitchenDesignBase, KitchenDesignOptions } from '../kitchen-design-base';
import { Publish } from '../../decorators';
import { skuAnnotationData } from './sku-annotation.data';
import { ColorRun, DoodlePoint, SkuAnnotationData, SkuAnnotationDataIn, StyleRun } from './types';
import { SkuAnnotationArrowhead, SkuAnnotationLineStyle, SkuAnnotationType } from './shared.enums';

export class SkuAnnotation extends KitchenDesignBase<SkuAnnotationDataIn> {
	#id: string;
	#type: SkuAnnotationType;
	#x: number;
	#y: number;
	#x2: number;
	#y2: number;
	#width: number;
	#height: number;
	#rotation: number;
	#text: string;
	#fontSize: number;
	#color: string;
	#backgroundColor: string;
	#textColor: string;
	#colorRuns: Array<ColorRun>;
	#styleRuns: Array<StyleRun>;
	#fontWeight: string;
	#fontStyle: string;
	#fontFamily: string;
	#textAlign: string;
	#verticalAlign: string;
	#fillOpacity: number;
	#strokeWidth: number;
	#lineStyle: SkuAnnotationLineStyle;
	#arrowhead: SkuAnnotationArrowhead;
	#points: Array<DoodlePoint>;
	#pageNumber: number;
	#groupId: string;
	#linkedIslandId: string;
	#createdBy: string;
	#createDate: string;

	constructor (
		contextEmail: string,
		params: Partial<SkuAnnotationData> | null = null,
		options: KitchenDesignOptions<SkuAnnotationDataIn> = {}
	) {
		super(BlueprintInstanceType.SkuAnnotation, options);
		const data = skuAnnotationData(contextEmail, params, options.dataIn || null);

		this.#id = data.id;
		this.#type = data.type;
		this.#x = data.x;
		this.#y = data.y;
		this.#x2 = data.x2;
		this.#y2 = data.y2;
		this.#width = data.width;
		this.#height = data.height;
		this.#rotation = data.rotation;
		this.#text = data.text;
		this.#fontSize = data.fontSize;
		this.#color = data.color;
		this.#backgroundColor = data.backgroundColor;
		this.#textColor = data.textColor;
		this.#colorRuns = data.colorRuns;
		this.#styleRuns = data.styleRuns;
		this.#fontWeight = data.fontWeight;
		this.#fontStyle = data.fontStyle;
		this.#fontFamily = data.fontFamily;
		this.#textAlign = data.textAlign;
		this.#verticalAlign = data.verticalAlign;
		this.#fillOpacity = data.fillOpacity;
		this.#strokeWidth = data.strokeWidth;
		this.#lineStyle = data.lineStyle;
		this.#arrowhead = data.arrowhead;
		this.#points = data.points;
		this.#pageNumber = data.pageNumber;
		this.#groupId = data.groupId;
		this.#linkedIslandId = data.linkedIslandId;
		this.#createdBy = data.createdBy;
		this.#createDate = data.createDate;
		this.setInitialized();
	}

	get id (): string { return this.#id; }
	get type (): SkuAnnotationType { return this.#type; }

	get x (): number { return this.#x; }
	@Publish('sku_annotation', 'updated')
	set x (value: number) {
		this.#x = value;
	}

	get y (): number { return this.#y; }
	@Publish('sku_annotation', 'updated')
	set y (value: number) {
		this.#y = value;
	}

	get x2 (): number { return this.#x2; }
	@Publish('sku_annotation', 'updated')
	set x2 (value: number) {
		this.#x2 = value;
	}

	get y2 (): number { return this.#y2; }
	@Publish('sku_annotation', 'updated')
	set y2 (value: number) {
		this.#y2 = value;
	}

	get width (): number { return this.#width; }
	@Publish('sku_annotation', 'updated')
	set width (value: number) {
		this.#width = value;
	}

	get height (): number { return this.#height; }
	@Publish('sku_annotation', 'updated')
	set height (value: number) {
		this.#height = value;
	}

	get rotation (): number { return this.#rotation; }
	@Publish('sku_annotation', 'updated')
	set rotation (value: number) {
		this.#rotation = value;
	}

	get text (): string { return this.#text; }
	@Publish('sku_annotation', 'updated')
	set text (value: string) {
		this.#text = value;
	}

	get fontSize (): number { return this.#fontSize; }
	@Publish('sku_annotation', 'updated')
	set fontSize (value: number) {
		this.#fontSize = value;
	}

	get color (): string { return this.#color; }
	@Publish('sku_annotation', 'updated')
	set color (value: string) {
		this.#color = value;
	}

	get backgroundColor (): string { return this.#backgroundColor; }
	@Publish('sku_annotation', 'updated')
	set backgroundColor (value: string) {
		this.#backgroundColor = value;
	}

	get textColor (): string { return this.#textColor; }
	@Publish('sku_annotation', 'updated')
	set textColor (value: string) {
		this.#textColor = value;
	}

	get colorRuns (): Array<ColorRun> { return this.#colorRuns; }
	@Publish('sku_annotation', 'updated')
	set colorRuns (value: Array<ColorRun>) {
		this.#colorRuns = value;
	}

	get styleRuns (): Array<StyleRun> { return this.#styleRuns; }
	@Publish('sku_annotation', 'updated')
	set styleRuns (value: Array<StyleRun>) {
		this.#styleRuns = value;
	}

	get fontWeight (): string { return this.#fontWeight; }
	@Publish('sku_annotation', 'updated')
	set fontWeight (value: string) {
		this.#fontWeight = value;
	}

	get fontStyle (): string { return this.#fontStyle; }
	@Publish('sku_annotation', 'updated')
	set fontStyle (value: string) {
		this.#fontStyle = value;
	}

	get fontFamily (): string { return this.#fontFamily; }
	@Publish('sku_annotation', 'updated')
	set fontFamily (value: string) {
		this.#fontFamily = value;
	}

	get textAlign (): string { return this.#textAlign; }
	@Publish('sku_annotation', 'updated')
	set textAlign (value: string) {
		this.#textAlign = value;
	}

	get verticalAlign (): string { return this.#verticalAlign; }
	@Publish('sku_annotation', 'updated')
	set verticalAlign (value: string) {
		this.#verticalAlign = value;
	}

	get fillOpacity (): number { return this.#fillOpacity; }
	@Publish('sku_annotation', 'updated')
	set fillOpacity (value: number) {
		this.#fillOpacity = value;
	}

	get strokeWidth (): number { return this.#strokeWidth; }
	@Publish('sku_annotation', 'updated')
	set strokeWidth (value: number) {
		this.#strokeWidth = value;
	}

	get lineStyle (): SkuAnnotationLineStyle { return this.#lineStyle; }
	@Publish('sku_annotation', 'updated')
	set lineStyle (value: SkuAnnotationLineStyle) {
		this.#lineStyle = value;
	}

	get arrowhead (): SkuAnnotationArrowhead { return this.#arrowhead; }
	@Publish('sku_annotation', 'updated')
	set arrowhead (value: SkuAnnotationArrowhead) {
		this.#arrowhead = value;
	}

	get points (): Array<DoodlePoint> { return this.#points; }
	@Publish('sku_annotation', 'updated')
	set points (value: Array<DoodlePoint>) {
		this.#points = value;
	}

	get pageNumber (): number { return this.#pageNumber; }
	@Publish('sku_annotation', 'updated')
	set pageNumber (value: number) {
		this.#pageNumber = value;
	}

	get groupId (): string { return this.#groupId; }
	@Publish('sku_annotation', 'updated')
	set groupId (value: string) {
		this.#groupId = value;
	}

	get linkedIslandId (): string { return this.#linkedIslandId; }
	@Publish('sku_annotation', 'updated')
	set linkedIslandId (value: string) {
		this.#linkedIslandId = value;
	}

	get createdBy (): string { return this.#createdBy; }
	get createDate (): Date { return new Date(this.#createDate); }

	public serialize (): SkuAnnotationData {
		return {
			id: this.#id,
			type: this.#type,
			x: this.#x,
			y: this.#y,
			x2: this.#x2,
			y2: this.#y2,
			width: this.#width,
			height: this.#height,
			rotation: this.#rotation,
			text: this.#text,
			fontSize: this.#fontSize,
			color: this.#color,
			backgroundColor: this.#backgroundColor,
			textColor: this.#textColor,
			colorRuns: this.#colorRuns,
			styleRuns: this.#styleRuns,
			fontWeight: this.#fontWeight,
			fontStyle: this.#fontStyle,
			fontFamily: this.#fontFamily,
			textAlign: this.#textAlign,
			verticalAlign: this.#verticalAlign,
			fillOpacity: this.#fillOpacity,
			strokeWidth: this.#strokeWidth,
			lineStyle: this.#lineStyle,
			arrowhead: this.#arrowhead,
			points: this.#points,
			pageNumber: this.#pageNumber,
			groupId: this.#groupId,
			linkedIslandId: this.#linkedIslandId,
			createdBy: this.#createdBy,
			createDate: this.#createDate
		};
	}
}
